import { ChevronRightIcon } from "@heroicons/react/16/solid"
import { HomeIcon } from "@src/icons"
import clsx from "clsx"
import Link from "next/link"
import { Fragment, type ReactNode } from "react"
import HelperPopover from "./HelperPopover"

type Props = {
  title: string
  subtitle?: ReactNode
  intro?: ReactNode
  /**
   * - Home is rendered automatically as the first item.
   * - Items are shown in order, separated by chevrons.
   * - The last item is treated as the current page.
   */
  breadcrumbs?: {
    href: string
    label: string
  }[]
  children?: ReactNode
}

const sharedClasses =
  "hover:text-gray-900 focus-visible:bg-gray-900/5 focus-visible:text-gray-900 focus-visible:outline-none dark:focus-visible:bg-white/5 dark:focus-visible:text-white dark:hover:text-white"

const PageHeader = ({
  title,
  subtitle,
  intro,
  children,
  breadcrumbs,
}: Props) => (
  <>
    {breadcrumbs?.length ? (
      <nav aria-label="Breadcrumbs" className="-ml-1 mb-5 sm:mb-7">
        <ol className="flex items-center gap-0.5">
          <li>
            <Link
              href="/home"
              className={clsx(
                "flex size-7 items-center justify-center rounded-lg text-gray-500 dark:text-gray-400",
                sharedClasses
              )}
            >
              <span className="sr-only">Home</span>
              <HomeIcon className="size-4.5 shrink-0" aria-hidden="true" />
            </Link>
          </li>
          {breadcrumbs.map(({ href, label }, index) => {
            const isLast = index === breadcrumbs.length - 1

            return (
              <Fragment key={href}>
                <ChevronRightIcon className="size-4 shrink-0 text-gray-400 dark:text-gray-500" />

                <li className="font-semibold text-sm/5">
                  <Link
                    href={href}
                    aria-current={isLast ? "page" : undefined}
                    className={clsx(
                      "rounded-lg px-1.5 py-1",
                      sharedClasses,
                      isLast
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400"
                    )}
                  >
                    {label}
                  </Link>
                </li>
              </Fragment>
            )
          })}
        </ol>
      </nav>
    ) : null}

    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="font-semibold text-gray-900 text-lg tracking-tight sm:text-xl dark:text-white">
            {title}
          </h1>

          {intro && <HelperPopover>{intro}</HelperPopover>}
        </div>
        {subtitle && (
          <p className="mt-0.5 text-pretty font-medium text-gray-500 text-sm sm:mt-1 dark:text-gray-400">
            {subtitle}
          </p>
        )}
      </div>
      {children && <div>{children}</div>}
    </div>
  </>
)

export default PageHeader
